import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { NavLink } from 'react-router';
import { Tooltip } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useAppStore } from '@/lib/store';
import { EntitySwitcher } from './EntitySwitcher';
import { Logo, BetaTag } from './Logo';
import { HELP_ICON, SETTINGS_ITEM, type NavItem } from './nav';
import { useNavItems } from './useNavItems';
import { badgeLabel, useNavBadges } from './useNavBadges';

const itemClass = (active: boolean, collapsed: boolean) =>
  cn(
    'relative flex min-h-10 items-center gap-3 rounded-md px-2.5 text-sm font-medium transition-colors duration-fast coarse:min-h-touch',
    collapsed && 'justify-center px-0',
    active ? 'bg-primary-soft text-primary' : 'text-text-muted hover:bg-surface-sunken hover:text-text',
  );

const SidebarLink = ({ item, collapsed, count }: { readonly item: NavItem; readonly collapsed: boolean; readonly count: number }) => {
  const Icon = item.icon;
  const link = (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) => itemClass(isActive, collapsed)}
      aria-label={collapsed ? item.label + (count && item.badge ? `, ${badgeLabel(item.badge, count)}` : '') : undefined}
    >
      <Icon className="size-[18px] shrink-0" aria-hidden />
      {collapsed ? null : <span className="flex-1 truncate">{item.label}</span>}
      {item.badge && count > 0 ? (
        collapsed ? (
          <span className="absolute right-2 top-2 size-2 rounded-full bg-accent" aria-hidden />
        ) : (
          <span
            className={cn(
              'rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums',
              item.badge === 'unreviewed' ? 'bg-accent-soft text-text' : 'bg-info-soft text-info',
            )}
          >
            <span aria-hidden>{count}</span>
            <span className="sr-only">{badgeLabel(item.badge, count)}</span>
          </span>
        )
      ) : null}
    </NavLink>
  );
  return collapsed ? (
    <Tooltip content={item.label} side="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
};

/** Desktop left navigation (hidden below md). Collapses to icons; state persists. */
export const Sidebar = () => {
  const collapsed = useAppStore((s) => s.sidebarCollapsed);
  const set = useAppStore((s) => s.set);
  const openHelp = useAppStore((s) => s.openHelp);
  const badges = useNavBadges();
  const navItems = useNavItems();
  const HelpIcon = HELP_ICON;
  return (
    <aside
      data-print-hide
      className={cn(
        'sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-border bg-surface md:flex',
        collapsed ? 'w-[72px]' : 'w-60',
      )}
      aria-label="Sidebar"
    >
      <div className={cn('flex h-16 items-center px-4', collapsed && 'justify-center px-0')}>
        <Logo collapsed={collapsed} />
        {!collapsed && <BetaTag className="ml-2" />}
      </div>
      <div className={cn('px-3 pb-3', collapsed && 'px-3')}>
        <EntitySwitcher collapsed={collapsed} />
      </div>
      <nav aria-label="Main" className="flex-1 overflow-y-auto px-3" data-tour="sidebar-nav">
        <ul className="flex flex-col gap-0.5">
          {navItems.map((item) => (
            <li key={item.id}>
              <SidebarLink item={item} collapsed={collapsed} count={item.badge ? badges[item.badge] : 0} />
            </li>
          ))}
        </ul>
      </nav>
      <div className="flex flex-col gap-0.5 border-t border-border p-3">
        <SidebarLink item={SETTINGS_ITEM} collapsed={collapsed} count={0} />
        <button type="button" onClick={() => openHelp(null)} className={itemClass(false, collapsed)} aria-label={collapsed ? 'Help' : undefined}>
          <HelpIcon className="size-[18px] shrink-0" aria-hidden />
          {collapsed ? null : <span className="flex-1 text-left">Help</span>}
        </button>
        <button
          type="button"
          onClick={() => set({ sidebarCollapsed: !collapsed })}
          className={itemClass(false, collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
        >
          {collapsed ? <PanelLeftOpen className="size-[18px]" aria-hidden /> : <PanelLeftClose className="size-[18px]" aria-hidden />}
          {collapsed ? null : <span className="flex-1 text-left">Collapse</span>}
        </button>
      </div>
    </aside>
  );
};
